import BigNumber from 'bignumber.js';
import { format, formatDate, parseISO } from 'date-fns';
import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import sortBy from 'lodash/sortBy';
import uniqWith from 'lodash/uniqWith';
import values from 'lodash/values';
import zip from 'lodash/zip';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryIsoCode } from '~/types/country';
import {
  Document,
  DocumentDirection,
  DocumentPaymentStatus,
  DocumentQuickFilter,
  DocumentSource,
  DocumentStatus,
  DocumentType,
  DocumentWithAttachments,
  EInvoiceType,
} from '~/types/document';
import {
  DocumentBuilderForm,
  PreviewAdditionalData,
  RelatedDocumentLine,
  RelatedDocumentType,
  TransportInformations,
} from '~/types/document-builder';
import { FlowPaymentStatus, InvoicePaymentFlow, PaymentMethod } from '~/types/flow';
import {
  CondizioniPagamento,
  DatiPagamento,
  EInvoicePaymentMethod,
  EsigibilitaIva,
  FatturaBody,
  ItalianEInvoice,
  SharedEInvoice,
  VatTotal,
} from '~/types/invoicing';
import { Money } from '~/types/money';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import { formatDateForAPI } from '~/utils/formatDate';
import {
  calculateCompetencies,
  calculateStampTotal,
  calculateVatItemsTotals,
  filterTaxableItems,
  findMaxVat,
  findMaxVatLine,
  itemsDiscountedGrossTotal,
  lineGrossTotals,
  lineNetTotals,
} from '~/utils/invoicing/items';
import {
  serializeRelatedDocument,
  serializeRelatedDocumentForDatiDdt,
  serializeTransportInformations,
} from '~/utils/invoicing/serializers';
import { socialSecurityTotals } from '~/utils/invoicing/social-security-totals';
import { calcWithholdingAmount } from '~/utils/invoicing/withholding';
import { sumMoney } from '~/utils/money';
import { documentTypeToRelatedDocument } from '~/utils/relatedDocument';
import { resolveVatExemption } from '~/utils/vat';

export interface InvoiceTotals {
  documentTotal: Money;
  totalToPay: Money;
  netTotal: Money;
  vatTotals: VatTotal[];
  socialSecurity?: {
    taxableAmount: Money;
    totalAmount: Money;
    vatAmount: Money;
  };
  alreadyPaidTotal: Money;
  shouldPayVat: boolean;
  stampTotal: Money;
}

export const draftTotals = (invoice: DocumentBuilderForm): InvoiceTotals => {
  const currency = invoice.currency;
  const fiscalRegime = invoice.fiscalRegime;
  const taxableItems = filterTaxableItems(invoice.items, fiscalRegime);

  const linesTotals = invoice.useGrossPrices ? taxableItems.map(lineGrossTotals) : taxableItems.map(lineNetTotals);
  const maxVatLine = findMaxVatLine(taxableItems);
  assertIsDefined(maxVatLine, 'There must be an item');
  const maxVat = findMaxVat(taxableItems);

  const socialSecurityTotal = socialSecurityTotals(invoice.socialSecurity, maxVat);
  const vatItems = calculateVatItemsTotals(invoice.items, currency, invoice.useGrossPrices);
  const stampTotal = calculateStampTotal(invoice.items, fiscalRegime);
  const withholdingTaxTotal = BigNumber.sum(0, ...invoice.withholdingTax.map(calcWithholdingAmount));
  const flowsPaidTotal = BigNumber.sum(
    0,
    ...invoice.flows.filter((x) => x.paymentStatus === FlowPaymentStatus.Paid).map((x) => x.amount),
  );
  const shouldPayVat = invoice.vatCollection !== EsigibilitaIva.Split && invoice.type !== DocumentType.SelfInvoice;

  const netTotal = calculateCompetencies(linesTotals, invoice.useGrossPrices, vatItems);

  const documentAmount = invoice.useGrossPrices
    ? itemsDiscountedGrossTotal(linesTotals)
    : netTotal.plus(sumMoney(...vatItems.map((v) => v.vatTotal)).amount);

  let totalToPay = shouldPayVat ? documentAmount : netTotal;

  if (socialSecurityTotal) {
    totalToPay = totalToPay.plus(socialSecurityTotal.amount);
    if (shouldPayVat) {
      totalToPay = totalToPay.plus(socialSecurityTotal.vatAmount);
    }
  }
  totalToPay = totalToPay.plus(stampTotal);
  totalToPay = totalToPay.minus(withholdingTaxTotal);

  const documentTotalWithSocialSecurity = socialSecurityTotal
    ? documentAmount.plus(socialSecurityTotal.total)
    : documentAmount;

  // The cassa previdenziale must be folded into EXACTLY ONE datiRiepilogo/VAT bucket
  // (the one that contains maxVatLine), matched by percentage + exemption + lawReference.
  // Matching on the exemption alone would add the cassa to every bucket sharing that
  // exemption, which happens when a bollo row carries a distinct lawReference and splits
  // the exempt group in two — doubling the cassa in the preview total. See TS-1529.
  const maxVatLineExemption = resolveVatExemption(maxVatLine.percentage, maxVatLine.taxExemption);
  const socialSecurityBucketIndex = socialSecurityTotal
    ? vatItems.findIndex(
        (vat) =>
          vat.vatPercentage === maxVatLine.percentage &&
          (vat.vatExemption ?? null) === (maxVatLineExemption ?? null) &&
          (vat.lawReference ?? null) === (maxVatLine.lawReference ?? null),
      )
    : -1;

  // maxVatLine comes from the taxable items, whose VAT group must always exist in vatItems
  // (computed from all items). If it doesn't, folding the cassa would silently drop it from
  // the VAT breakdown and undercount the preview total, so fail loud instead. See TS-1529.
  if (socialSecurityTotal && socialSecurityBucketIndex < 0) {
    throw new Error('social security must map to a VAT bucket');
  }

  const vatTotals = socialSecurityTotal
    ? vatItems.map((vat, index) => {
        if (index !== socialSecurityBucketIndex) {
          return vat;
        }

        const vatAmount = vat.vatTotal.amount;
        const netAmount = vat.netTotal.amount;

        return {
          ...vat,
          vatTotal: {
            amount: socialSecurityTotal.vatAmount.plus(vatAmount).toFixed(),
            currency: vat.vatTotal.currency,
          },
          netTotal: {
            amount: socialSecurityTotal.amount.plus(netAmount).toFixed(),
            currency: vat.netTotal.currency,
          },
        };
      })
    : vatItems;

  return {
    documentTotal: { amount: documentTotalWithSocialSecurity.decimalPlaces(2).toFixed(), currency },
    totalToPay: { amount: totalToPay.decimalPlaces(2).toFixed(), currency },
    netTotal: { amount: netTotal.decimalPlaces(2).toFixed(), currency },
    vatTotals,
    alreadyPaidTotal: { amount: flowsPaidTotal.decimalPlaces(2).toFixed(), currency },
    socialSecurity: socialSecurityTotal
      ? {
          taxableAmount: { amount: socialSecurityTotal.taxableAmount.toFixed(), currency },
          totalAmount: { amount: socialSecurityTotal.amount.decimalPlaces(2).toFixed(), currency },
          vatAmount: { amount: socialSecurityTotal.vatAmount.decimalPlaces(2).toFixed(), currency },
        }
      : undefined,
    shouldPayVat,
    stampTotal: { amount: stampTotal.decimalPlaces(2).toFixed(), currency },
  };
};

const paymentMethodForInvoice = (paymentMethod: PaymentMethod | undefined | null): EInvoicePaymentMethod | null => {
  switch (paymentMethod) {
    case PaymentMethod.Transfer:
      return EInvoicePaymentMethod.Bonifico;
    case PaymentMethod.Cash:
      return EInvoicePaymentMethod.Contanti;
    case PaymentMethod.Card:
      return EInvoicePaymentMethod.CartaDiPagamento;
    case PaymentMethod.Check:
      return EInvoicePaymentMethod.Assegno;
    case PaymentMethod.Postal:
      return EInvoicePaymentMethod.BollettinoPostale;
    case PaymentMethod.Riba:
      return EInvoicePaymentMethod.RIBA;
    case PaymentMethod.Sdd:
      return EInvoicePaymentMethod.SEPADirectDebit;
    case PaymentMethod.FiscalCredit:
      return EInvoicePaymentMethod.QuietanzaErario;
  }
  return null;
};

interface InvoiceDownloadNameParams {
  counterpart: string;
  creationDate: Date;
  documentType: DocumentType;
  number: string | null;
  provisionalNumber?: string;
  status: DocumentStatus;
}

export const useInvoiceDownloadName = () => {
  const { t } = useTranslation('documents');

  return useCallback(
    ({ counterpart, creationDate, documentType, number, provisionalNumber, status }: InvoiceDownloadNameParams) => {
      const type = t(`type.${documentType}`);
      const resolvedNumber = number ?? provisionalNumber ?? formatDate(creationDate, 'dd-MM-yyyy');

      if (status === DocumentStatus.Draft) {
        return t('builder.preview.download_filename_draft', { type, number: resolvedNumber, counterpart });
      } else {
        return t('builder.preview.download_filename', { type, number: resolvedNumber, counterpart });
      }
    },
    [t],
  );
};

export function defaultEInvoiceType(documentType: DocumentType): EInvoiceType | null {
  switch (documentType) {
    case DocumentType.Invoice:
      return EInvoiceType.Fattura;
    case DocumentType.CreditNote:
      return EInvoiceType.NotaDiCredito;
    case DocumentType.DebitNote:
      return EInvoiceType.NotaDiDebito;
    case DocumentType.Parcel:
      return EInvoiceType.Parcella;
    case DocumentType.SelfInvoice:
      return EInvoiceType.AutofatturaReverseCharge;
    case DocumentType.DeliveryNote:
      return null;
    default:
      return null;
  }
}

export function isEInvoiceTypeCompatible(eInvoiceType: EInvoiceType | null, documentType: DocumentType): boolean {
  if (!eInvoiceType) {
    return false;
  }
  switch (documentType) {
    case DocumentType.Invoice:
      return [
        EInvoiceType.Fattura,
        EInvoiceType.AccontoFattura,
        EInvoiceType.FatturaDifferita,
        EInvoiceType.AutofatturaCessioneDiBeniAmmortizzabili,
      ].includes(eInvoiceType);
    case DocumentType.SelfInvoice:
      return [
        EInvoiceType.Fattura,
        EInvoiceType.AutofatturaBeniEU,
        EInvoiceType.AutofatturaBeniEsteri,
        EInvoiceType.AutofatturaServiziEsteri,
        EInvoiceType.AutofatturaReverseCharge,
        EInvoiceType.CommunicationOmittedIrregularInvoice,
        EInvoiceType.AutofatturaCessioneDiBeniAmmortizzabili,
        EInvoiceType.AutofatturaPerAutoconsumo,
      ].includes(eInvoiceType);
    case DocumentType.CreditNote:
      return EInvoiceType.NotaDiCredito === eInvoiceType;
    case DocumentType.DebitNote:
      return EInvoiceType.NotaDiDebito === eInvoiceType;
    case DocumentType.Parcel:
      return EInvoiceType.Parcella === eInvoiceType;
    default:
      return false;
  }
}

export function isForeignSelfInvoiceType(eInvoiceType: EInvoiceType | null): boolean {
  if (!eInvoiceType) {
    return false;
  }
  return [
    EInvoiceType.AutofatturaServiziEsteri,
    EInvoiceType.AutofatturaBeniEU,
    EInvoiceType.AutofatturaBeniEsteri,
  ].includes(eInvoiceType);
}

export function canDoCreditNote(document: Document): boolean {
  if (!document.isEInvoice) {
    return false;
  }
  if (document.documentType !== DocumentType.Invoice) {
    return false;
  }
  if (document.direction !== DocumentDirection.issued) {
    return false;
  }
  return [DocumentStatus.Created, DocumentStatus.Sent, DocumentStatus.Delivered, DocumentStatus.Not_Delivered].includes(
    document.status,
  );
}

export function canDoInvoice(document: Document): boolean {
  if (document.documentType !== DocumentType.DeliveryNote && document.documentType !== DocumentType.Quote) {
    return false;
  }
  return [DocumentStatus.Created, DocumentStatus.Sent, DocumentStatus.Delivered, DocumentStatus.Not_Delivered].includes(
    document.status,
  );
}

export function canDoDeliveryNote(document: Document): boolean {
  if (
    ![
      DocumentType.Invoice,
      DocumentType.DebitNote,
      DocumentType.CreditNote,
      DocumentType.SelfInvoice,
      DocumentType.Parcel,
      DocumentType.Other,
      DocumentType.Quote,
    ].includes(document.documentType)
  ) {
    return false;
  }
  return [DocumentStatus.Created, DocumentStatus.Sent, DocumentStatus.Delivered, DocumentStatus.Not_Delivered].includes(
    document.status,
  );
}

export function canDoSend(document: Document): boolean {
  return (
    document.documentType !== DocumentType.Bill &&
    ![DocumentStatus.Discarded, DocumentStatus.Refused].includes(document.status)
  );
}

function numberPattern(number: string): string {
  return number.replace(/\d+/g, '~');
}

export interface DocumentNumberWithSectional {
  number: string;
  sectionalId: string | null;
  progressive: bigint | null;
}

export function highestInvoiceNumbersBySectional(
  numbers: DocumentNumberWithSectional[],
): DocumentNumberWithSectional[] {
  const withProgressive = numbers.filter((n) => n.progressive !== null);
  const grouped = values(groupBy(withProgressive, ({ sectionalId }) => sectionalId));
  return filter(
    map(grouped, (group) => maxBy(group, 'progressive')),
    isDefined,
  );
}

export function highestInvoiceNumbersByPattern(numbers: { number: string; source: Document['source'] }[]): string[] {
  const grouped = values(groupBy(numbers, ({ number }) => numberPattern(number)));
  const highest = filter(map(grouped, getHighestInvoiceNumber), isDefined);
  return highest;
}

const generateYearArray = (start: number, end: number): string[] => {
  const yearArray = [];

  for (let year = start; year <= end; year++) {
    const date = new Date(year, 0, 1);
    yearArray.push(format(date, 'yyyy'), format(date, 'yy'));
  }

  return yearArray;
};

interface Subnumber {
  number: string;
  index: number;
}

const findYears = ({
  numbers,
}: {
  numbers: {
    number: string;
    source: DocumentSource;
    subnumbers: Subnumber[];
  }[];
}): Subnumber[] | null[] => {
  const yearCandidates: Subnumber[] = [];
  const validYears = new Set(generateYearArray(2019, 2035));

  for (const entry of numbers) {
    // find all numbers that can correspond to a year
    for (const sub of entry.subnumbers) {
      if (validYears.has(sub.number)) {
        yearCandidates.push(sub);
      }
    }
  }

  const groupedByIndex = groupBy(yearCandidates, 'index');

  const result: Subnumber[] = [];

  Object.values(groupedByIndex).forEach((group) => {
    // Check if the group has the same length as the 'numbers' array
    if (group.length === numbers.length) {
      // Collect distinct elements using uniqWith to compare them based on equality
      const uniqueElement = uniqWith(group, isEqual);
      result.push(...uniqueElement);
    }
  });
  return result;
};

const getSubnumbers = (numbers: { number: string; source: Document['source'] }[]) => {
  const subNumber = numbers.map(({ number, source }) => ({
    number,
    source,
    subnumbers: sortBy(
      [...number.matchAll(/\d+/g)].map((match, index) => ({
        number: match[0],
        index,
      })),
      ['index'],
    ),
  }));

  const year = findYears({ numbers: subNumber });

  //put all the possible year subnumbers to the first position
  if (year.length) {
    subNumber.forEach((el) => {
      const inA2 = el.subnumbers.filter((itemA1) => year.some((itemA2) => isEqual(itemA1, itemA2)));
      const notInA2 = el.subnumbers.filter((itemA1) => !year.some((itemA2) => isEqual(itemA1, itemA2)));
      el.subnumbers = [...inA2, ...notInA2];
    });
  }

  return subNumber;
};

export function getHighestInvoiceNumber(numbers: { number: string; source: Document['source'] }[]): string | null {
  if (numbers.length === 0) {
    return null;
  }

  const withSubnumbers = getSubnumbers(numbers);
  const sorted = [...withSubnumbers].sort((a, b) => {
    if (numberPattern(a.number) !== numberPattern(b.number)) {
      if (a.source === DocumentSource.User && b.source !== DocumentSource.User) {
        return -1;
      }
      if (a.source !== DocumentSource.User && b.source === DocumentSource.User) {
        return 1;
      }
    }
    if (a.subnumbers.length > b.subnumbers.length) {
      return -1;
    }
    if (a.subnumbers.length < b.subnumbers.length) {
      return 1;
    }

    const zipped = zip(a.subnumbers, b.subnumbers);
    const firstDifferent = zipped.find(([subA, subB]) => !isEqual(subA, subB));
    if (!firstDifferent) {
      return 0;
    }
    if (!firstDifferent[0]) {
      return 1;
    }
    if (!firstDifferent[1]) {
      return -1;
    }
    if (parseInt(firstDifferent[0].number, 10) > parseInt(firstDifferent[1].number, 10)) {
      return -1;
    }
    return 1;
  });

  return sorted[0]!.number;
}

export function increaseInvoiceNumber(invoiceNumber: string | null): string | null {
  if (!invoiceNumber?.match(/^\d+$/)) {
    return null;
  }
  const parsed = parseInt(invoiceNumber, 10);
  if (isNaN(parsed)) {
    return null;
  }
  return (parsed + 1).toString();
}

export function splitMotivation(motivation: string): string[] | null {
  const chunks = motivation.match(/.{1,200}/g);
  if (!chunks) {
    return null;
  }
  return chunks;
}

export function documentFinalizeAdditionalData(
  document: DocumentWithAttachments,
  eInvoice: ItalianEInvoice,
): PreviewAdditionalData {
  const getDocumentType = (document: DocumentWithAttachments, eInvoice: ItalianEInvoice): DocumentType => {
    if (
      eInvoice.fatturaElettronicaBody[0]?.datiGenerali.datiGeneraliDocumento.tipoDocumento === EInvoiceType.Fattura &&
      document.direction === DocumentDirection.received
    ) {
      return DocumentType.SelfInvoice;
    }
    return document.documentType;
  };

  return {
    companyLogo: document.company.logoUrls,
    isElectronic: document.isEInvoice,
    notes: document.notes,
    status: document.status,
    type: getDocumentType(document, eInvoice),
  };
}

export function documentPreviewAdditionalData(document: DocumentWithAttachments): PreviewAdditionalData {
  return {
    companyLogo: document.company.logoUrls,
    isElectronic: document.isEInvoice,
    notes: document.notes,
    status: document.status,
    type: document.documentType,
  };
}

export function sharedInvoicePreviewAdditionalData(invoice: SharedEInvoice['data']): PreviewAdditionalData {
  return {
    companyLogo: invoice.company.logoUrls,
    isElectronic: invoice.document.isEInvoice,
    notes: invoice.document.notes,
    status: invoice.document.status,
    type: invoice.document.type,
  };
}

export const SDI_code = import.meta.env.VITE_SDI_CODE;

export function datiPagamentoFromFlows(flows: InvoicePaymentFlow[]): DatiPagamento[] {
  const condizioniPagamento = flows.length > 1 ? CondizioniPagamento.Installments : CondizioniPagamento.SinglePayment;

  return flows.map((flow) => ({
    condizioniPagamento:
      flow.paymentStatus === FlowPaymentStatus.Paid ? CondizioniPagamento.PrePayment : condizioniPagamento,
    dettaglioPagamento: [
      {
        remoteId: flow.id,
        accountId: flow.account?.id ?? null,
        dataScadenzaPagamento: flow.paymentDate,
        iban: flow.paymentAccount,
        importoPagamento: flow.amount.amount,
        istitutoFinanziario: flow.accountInstitution,
        modalitaPagamento: paymentMethodForInvoice(flow.paymentMethod) ?? EInvoicePaymentMethod.Bonifico,
      },
    ],
  }));
}

// Returns a copy of the e-invoice with the first body's datiPagamento rebuilt from the given
// flows, so an invoice paid after emission reflects payment. Returns the input unchanged when
// there are no flows (e.g. the public payload predates flow exposure).
export function eInvoiceWithFlowsPayments(eInvoice: ItalianEInvoice, flows: InvoicePaymentFlow[]): ItalianEInvoice {
  const [firstBody] = eInvoice.fatturaElettronicaBody;
  if (!flows.length || !firstBody) return eInvoice;

  const datiPagamento = datiPagamentoFromFlows(flows);

  return {
    ...eInvoice,
    fatturaElettronicaBody: eInvoice.fatturaElettronicaBody.map((body, index) =>
      index === 0 ? { ...body, datiPagamento } : body,
    ),
  };
}

export const isDDT = (documentType: DocumentType) => {
  return documentType === DocumentType.DeliveryNote;
};

export const isQuote = (documentType: DocumentType) => {
  return documentType === DocumentType.Quote;
};

export const isSelfInvoice = (documentType: DocumentType) => {
  return documentType === DocumentType.SelfInvoice;
};

const isInvoice = (documentType: DocumentType) => {
  return documentType === DocumentType.Invoice;
};

export const canToggleElectronic = (documentType: DocumentType, status: DocumentStatus) => {
  if (isDDT(documentType)) return false;

  if (isQuote(documentType)) return false;

  if (isSelfInvoice(documentType)) return false;

  return canChangeType(status);
};

export const canChangeType = (status: DocumentStatus) => {
  if ([DocumentStatus.Draft, DocumentStatus.Discarded].includes(status)) return true;
};

export const alignIsEInvoice = (current: boolean, documentType: DocumentType) => {
  if (isDDT(documentType)) return false;
  if (isQuote(documentType)) return false;
  if (isSelfInvoice(documentType)) return true;
  if (isInvoice(documentType)) return true;

  return current;
};

export const getDocumentFilterStatus = ({
  status,
  paymentStatus,
}: {
  status: DocumentStatus[];
  paymentStatus?: DocumentPaymentStatus;
}): DocumentQuickFilter => {
  if (
    isEqual(status, [
      DocumentStatus.Created,
      DocumentStatus.Sent,
      DocumentStatus.Delivered,
      DocumentStatus.Not_Delivered,
    ]) &&
    paymentStatus === DocumentPaymentStatus.ToPay
  ) {
    return DocumentQuickFilter.ToCacheOut;
  }
  if (isEqual(status, [DocumentStatus.Draft])) {
    return DocumentQuickFilter.Draft;
  }
  if (isEqual(status, [DocumentStatus.Discarded, DocumentStatus.Refused])) {
    return DocumentQuickFilter.Error;
  }
  if (
    isEqual(status, [
      DocumentStatus.Created,
      DocumentStatus.Sent,
      DocumentStatus.Delivered,
      DocumentStatus.Not_Delivered,
    ])
  ) {
    return DocumentQuickFilter.Finalized;
  }

  return DocumentQuickFilter.Finalized; //return a default value
};

const emptyFatturaBody: FatturaBody = {
  datiGenerali: {
    datiGeneraliDocumento: {
      arrotondamento: null,
      art73: null,
      data: formatDateForAPI(new Date()),
      causale: null,
      divisa: 'EUR',
      numero: null,
      tipoDocumento: EInvoiceType.Fattura,
      scontoMaggiorazione: [],
      importoTotaleDocumento: null,
      datiCassaPrevidenziale: [],
      datiRitenuta: [],
      datiBollo: null,
    },
    datiContratto: [],
    datiConvenzione: [],
    datiOrdineAcquisto: [],
    datiDdt: [],
    datiFattureCollegate: [],
    datiTrasporto: null,
    fatturaPrincipale: null,
    datiRicezione: [],
    useGrossPrices: null,
  },
  datiBeniServizi: {
    datiRiepilogo: [],
    dettaglioLinee: [],
  },
  datiPagamento: [],
};
export const prepareCreateCreditNoteData = (
  document: DocumentWithAttachments,
  eInvoice: ItalianEInvoice,
): ItalianEInvoice => {
  const body = eInvoice.fatturaElettronicaBody[0]!;

  const newBody: FatturaBody = {
    ...emptyFatturaBody,
    datiGenerali: {
      ...emptyFatturaBody.datiGenerali,
      datiGeneraliDocumento: {
        ...body.datiGenerali.datiGeneraliDocumento,
        tipoDocumento: EInvoiceType.NotaDiCredito,
        data: formatDateForAPI(new Date()),
      },
      datiFattureCollegate: document.number
        ? [
            {
              codiceCig: '',
              codiceCup: '',
              codiceCommessaConvenzione: '',
              numItem: null,
              riferimentoNumeroLinea: null,
              idDocumento: document.number,
              data: document.creationDate,
            },
          ]
        : [],
    },
    datiPagamento: [
      {
        condizioniPagamento: CondizioniPagamento.SinglePayment,
        dettaglioPagamento: [
          {
            remoteId: null,
            accountId: null,
            dataScadenzaPagamento: formatDateForAPI(new Date()),
            iban: null,
            importoPagamento: document.grossAmount.amount,
            istitutoFinanziario: null,
            modalitaPagamento: EInvoicePaymentMethod.Bonifico,
          },
        ],
      },
    ],
    datiBeniServizi: body.datiBeniServizi,
  };
  return {
    ...eInvoice,
    fatturaElettronicaBody: [newBody],
  };
};

const fromDocumentToRelatedDocuments = (document: DocumentWithAttachments) => {
  const relatedDocumentType = documentTypeToRelatedDocument(document.documentType);

  const relatedDocumentLine: RelatedDocumentLine | null = document.number
    ? {
        dbDocumentId: document.id,
        type: relatedDocumentType,
        documentNumber: document.number,
        date: parseISO(document.creationDate),
        CIGCode: '',
        CUPCode: '',
        orderCode: '',
      }
    : null;

  const relatedOrdineAcquisto =
    relatedDocumentLine !== null && relatedDocumentType === RelatedDocumentType.OrdineAcquisto
      ? [serializeRelatedDocument(relatedDocumentLine)]
      : emptyFatturaBody.datiGenerali.datiOrdineAcquisto;

  const relatedFattureCollegate =
    relatedDocumentLine !== null && relatedDocumentType === RelatedDocumentType.FatturaCollegata
      ? [serializeRelatedDocument(relatedDocumentLine)]
      : emptyFatturaBody.datiGenerali.datiFattureCollegate;

  const relatedDdt =
    relatedDocumentLine !== null && relatedDocumentType === RelatedDocumentType.Ddt
      ? [serializeRelatedDocumentForDatiDdt(relatedDocumentLine)]
      : emptyFatturaBody.datiGenerali.datiDdt;

  return {
    relatedOrdineAcquisto,
    relatedFattureCollegate,
    relatedDdt,
  };
};
interface CompanyDestination {
  address: string | null;
  postalCode: string | null;
  city: string | null;
  country: CountryIsoCode | null;
}
export const prepareCreateDeliveryNoteFromAsideData = (
  document: DocumentWithAttachments,
  eInvoice: ItalianEInvoice,
  destination: CompanyDestination,
): ItalianEInvoice => {
  const body = eInvoice.fatturaElettronicaBody[0]!;

  const { relatedOrdineAcquisto, relatedFattureCollegate, relatedDdt } = fromDocumentToRelatedDocuments(document);

  const transportInformations: TransportInformations = {
    destination: {
      address: destination.address ?? '',
      postalCode: destination.postalCode ?? '',
      city: destination.city ?? '',
      country: destination.country ?? 'IT',
    },
    transportReason: null,
    courier: null,
    packsNumber: null,
    weight: null,
  };

  const newBody: FatturaBody = {
    ...emptyFatturaBody,
    datiGenerali: {
      ...emptyFatturaBody.datiGenerali,
      datiGeneraliDocumento: {
        ...emptyFatturaBody.datiGenerali.datiGeneraliDocumento,
        tipoDocumento: EInvoiceType.Fattura,
        data: formatDateForAPI(new Date()),
        importoTotaleDocumento: body.datiGenerali.datiGeneraliDocumento.importoTotaleDocumento,
        datiBollo: body.datiGenerali.datiGeneraliDocumento.datiBollo,
      },
      datiOrdineAcquisto: relatedOrdineAcquisto,
      datiFattureCollegate: relatedFattureCollegate,
      datiDdt: relatedDdt,
      datiTrasporto: serializeTransportInformations(transportInformations),
    },
    datiPagamento: [],
    datiBeniServizi: body.datiBeniServizi,
  };
  return {
    ...eInvoice,
    fatturaElettronicaBody: [newBody],
  };
};

export const prepareCreateInvoiceFromAsideData = (
  document: DocumentWithAttachments,
  eInvoice: ItalianEInvoice,
): ItalianEInvoice => {
  const body = eInvoice.fatturaElettronicaBody[0]!;

  const { relatedOrdineAcquisto, relatedFattureCollegate, relatedDdt } = fromDocumentToRelatedDocuments(document);

  const newBody: FatturaBody = {
    ...body,
    datiGenerali: {
      ...emptyFatturaBody.datiGenerali,
      datiGeneraliDocumento: body.datiGenerali.datiGeneraliDocumento,
      datiOrdineAcquisto: relatedOrdineAcquisto,
      datiFattureCollegate: relatedFattureCollegate,
      datiDdt: relatedDdt,
    },
    datiPagamento: [
      {
        condizioniPagamento: CondizioniPagamento.SinglePayment,
        dettaglioPagamento: [
          {
            remoteId: null,
            accountId: null,
            dataScadenzaPagamento: formatDateForAPI(new Date()),
            iban: null,
            importoPagamento: document.grossAmount.amount,
            istitutoFinanziario: null,
            modalitaPagamento: EInvoicePaymentMethod.Bonifico,
          },
        ],
      },
    ],
    datiBeniServizi: body.datiBeniServizi,
  };
  return {
    ...eInvoice,
    fatturaElettronicaBody: [newBody],
  };
};

export const getEInvoiceBody = (eInvoice: ItalianEInvoice, number: string): FatturaBody => {
  return (
    eInvoice.fatturaElettronicaBody.find(
      (body) => body.datiGenerali.datiGeneraliDocumento.numero?.toLowerCase() === number.toLowerCase(),
    ) || eInvoice.fatturaElettronicaBody[0]!
  );
};
